import { numberToCurrencyString } from 'further-ui/utils';
import PieChart, { useStyles as usePieChartStyles } from 'components/PieChart';
import useFirmPortfolioBySector from 'hooks/data/dashboards/useFirmPortfolioBySector';
import { makeStyles } from 'tss-react/mui';
import { Skeleton, Typography } from '@mui/material';
import LoadingError from '../LoadingError';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const useStyles = makeStyles()((theme) => ({
  heading: {
    textAlign: 'center',
  },
  title: {
    fontSize: '16px',
  },
  subTitleStyle: {
    marginBottom: 0,
    marginTop: 4,
    fontSize: 12,
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightRegular as any,
  },
  skeletonContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  pieChartSkeleton: {
    margin: '40px 0',
  },
  labelSkeleton: {
    margin: '5px 0',
  },
}));

type Props = {
  title: string;
  subtitle: string;
  firmId?: string;
  preFetchedData?: Array<{
    categoryKey: string;
    categoryCurrentValue: number;
    categoryInitialInvestment: number;
    companiesInCategory: number;
  }>;
};

const CompanyCategoryPieChart: React.FC<Props> = ({
  title,
  subtitle,
  firmId,
  preFetchedData,
}) => {
  const { classes: classnames } = useStyles();
  const { classes: pieChartClassnames } = usePieChartStyles();

  const theme = useTheme();
  const [outerRadius, innerRadius] = useMediaQuery(
    theme.breakpoints.down('xl'),
    { noSsr: true },
  )
    ? [80, 60]
    : [110, 80];

  const { data, isFetching, isError } = useFirmPortfolioBySector({
    firmId,
    enabled: !preFetchedData,
  });

  const deploymentData = preFetchedData ? preFetchedData : data;

  const legendItems = Math.min(deploymentData?.length || 0, 10); // the pie chart has a maximum of 10 items in the legend
  const legendRowsEstimated = Math.ceil(legendItems / 2);
  const height = 300 + legendRowsEstimated * 20;

  if (isError) {
    return <LoadingError height={height} showCard={false} />;
  }

  return (
    <>
      <div className={classnames.heading}>
        <Typography variant="h4" component="div" className={classnames.title}>
          {title}
        </Typography>
        <Typography
          variant="h5"
          className={classnames.subTitleStyle}
          gutterBottom
          component="div"
        >
          {subtitle}
        </Typography>
      </div>
      {isFetching ? (
        <div className={classnames.skeletonContainer}>
          <Skeleton
            variant="circular"
            height={outerRadius * 2}
            width={outerRadius * 2}
            animation="wave"
            className={classnames.pieChartSkeleton}
          />
          <Skeleton
            variant="text"
            animation="wave"
            width="100%"
            className={classnames.labelSkeleton}
          />
          <Skeleton
            variant="text"
            animation="wave"
            width="100%"
            className={classnames.labelSkeleton}
          />
          <Skeleton
            variant="text"
            animation="wave"
            width="100%"
            className={classnames.labelSkeleton}
          />
        </div>
      ) : (
        <PieChart
          data={deploymentData?.map((item) => ({
            key: item.categoryKey,
            value: item.categoryCurrentValue,
            companiesInCategory: item.companiesInCategory,
            categoryInitialInvestment: item.categoryInitialInvestment,
          }))}
          height={height}
          outerRadius={outerRadius}
          innerRadius={innerRadius}
          noDataMessage="No subscriptions yet"
          CustomTooltip={({ payload }) => {
            const item = payload?.[0];
            if (!item) return null;
            return (
              <div className={pieChartClassnames.tooltipWrapper}>
                <p>
                  <strong>{item.name}</strong>
                </p>
                <p>
                  Current value:{' '}
                  {numberToCurrencyString(item.value ?? 0, {
                    hidePenniesIfInteger: true,
                  })}
                </p>
                <p>
                  Initial subscription:{' '}
                  {numberToCurrencyString(
                    item.payload?.categoryInitialInvestment,
                    { hidePenniesIfInteger: true },
                  )}
                </p>
                <p>
                  Companies in this category:{' '}
                  {item.payload?.companiesInCategory}
                </p>
              </div>
            );
          }}
        />
      )}
    </>
  );
};

export default CompanyCategoryPieChart;
